import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import {
  useAuth,
  useI18N,
  usePostFlagNote,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData, businessNotesAll } from "common/types";
import Zoom from "react-medium-image-zoom";
import AnalyticsService from "@/services/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

type FlagVenueNoteProps = {
  note?: businessNotesAll;
  onClose: () => void;
  venueDetails: any;
};

function FlagVenueNote({ note, onClose, venueDetails }: FlagVenueNoteProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();
  const { user } = useAuth();

  const showToast = useCustomToast();

  const [noteText, setNoteText] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isZoomedIn = useZoomDetection();
  const { data, error, mutate, status } = usePostFlagNote();

  useEffect(() => {
    if (error) {
      console.log("useFlagPhoto", error);

      AnalyticsService.trackGAEventHandler("flagAccessibilityReportError");
      AnalyticsService.trackMx("flagAccessibilityReportError", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      console.log("useFlagPhoto", data);
      showToast(data.message, "success");
      setAttachedImages([]);
      setNoteText("");
      onClose();
    }
  }, [data, isDark]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (noteText.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    if (attachedImages.length === 0) {
      setErrorMessage("Please add at least one image");
      return;
    }
    setErrorMessage(null);

    console.log("submit", noteText, attachedImages);

    AnalyticsService.trackGAEventHandler("flagVenueNoteCreate");
    AnalyticsService.trackMx("flagVenueNoteCreate", {
      VenueId: venueDetails.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.types,
      ReviewerName: user?.name,
    });

    mutate({
      noteId: note.business_note_text_id.toString(),
      text: noteText,
      photos: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={true}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {status === "pending" && <Loader />}
      <section
        style={modalStyle}
        className={cn(modalClassName)}
        role="none"
        aria-label={i18n.get("flagAccessibility")}
        ref={firstFocusRef}
      >
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <StandardButton
            onClick={() => {
              setAttachedImages([]);
              setNoteText("");
              onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <div className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("flagVenueNote")}
          </div>
          <div className="flex-shrink-0 w-10"></div>{" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {venueDetails.name}
                </div>
                <h1 className="self-stretch text-fg-neutral-strong title-s-semibold">
                  {i18n.get("helpUsCatchEveryError")}
                </h1>
              </div>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                {i18n.get("flagVenueNoteMessage")}
              </div>

              <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
            </div>

            <div className="self-stretch md:px-1 rounded-lg justify-start items-center gap-4 inline-flex">
              <div className="w-[24px] h-[24px] relative">
                <img
                  src={isDark ? "/images/Info_dark.svg" : "/images/Info.svg"}
                  alt=""
                  aria-hidden="true"
                  className="w-6 h-6 self-start cursor-pointer"
                />
              </div>
              <div className="h-14 flex-col justify-center items-start gap-1 inline-flex">
                <div className="w-full title-s-semibold text-fg-neutral-strong">
                  {i18n.get("venueNote")}
                </div>
                <div className="label-m-medium">{note.action_title}</div>
              </div>
            </div>

            <div className="border w-full border-border-token-brand-minimal mb-2 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  {i18n.get("tellUsWhatIsIncorrect")}
                </label>
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhatIsIncorrect")}
                  className="scrollbar-thin h-28"
                  value={noteText}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNoteText(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="label-s-medium text-fg-neutral-subtle mt-3"
                >
                  <span className={noteText.length < 5 ? "text-red-600" : ""}>
                    {noteText.length}
                  </span>
                  /150 Characters - at least 5 characters required
                </div>

                {errorMessage && (
                  <div className="text-red-600" role="alert">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto" tabIndex={0}>
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
              >
                <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={noteText.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit flag"
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}

export default FlagVenueNote;
